import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
} from "@mui/material";
import { AnalyticsOutlined, Refresh } from "@mui/icons-material";
import { Button, Typography } from "Atoms";
import {
  analyzeTestSpecificationAPI,
  getTestSpecificationByNodeIdAPI,
  getTestSpecificationStatusAPI,
} from "Zustand/services/projectsService";

type SpecificationStatus = {
  progress: string;
  stage: string;
  stage_current: number;
  stage_total: number;
  message: string;
};

type Props = {
  testSpecificationNodeId: string;
};

export default function BasicInfo({ testSpecificationNodeId }: Props) {
  const [detail, setDetail] = useState<any | null>(null);
  const [status, setStatus] = useState<SpecificationStatus | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);

  const loadDetail = useCallback(async () => {
    if (!testSpecificationNodeId) return;
    setLoadingDetail(true);
    try {
      const response = await getTestSpecificationByNodeIdAPI(
        testSpecificationNodeId
      );
      setDetail(response?.testspecification ?? response);
    } finally {
      setLoadingDetail(false);
    }
  }, [testSpecificationNodeId]);

  const loadStatus = useCallback(async () => {
    if (!testSpecificationNodeId) return;
    setLoadingStatus(true);
    try {
      const response = await getTestSpecificationStatusAPI(
        testSpecificationNodeId
      );
      setStatus(response);
    } finally {
      setLoadingStatus(false);
    }
  }, [testSpecificationNodeId]);

  const handleAnalyze = async () => {
    if (!testSpecificationNodeId) return;
    setAnalyzing(true);
    try {
      await analyzeTestSpecificationAPI(testSpecificationNodeId);
      await loadStatus();
    } finally {
      setAnalyzing(false);
    }
  };

  useEffect(() => {
    loadDetail();
    loadStatus();
  }, [loadDetail, loadStatus]);

  return (
    <Box>
      <Paper sx={{ p: 3 }} elevation={3}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
          gap={2}
          flexWrap="wrap"
        >
          <Box>
            <Typography variant="h5">
              {loadingDetail
                ? "Loading test specification..."
                : detail?.name || "Test Specification"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {detail?.description || "No description available"}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<AnalyticsOutlined />}
              onClick={handleAnalyze}
              disabled={analyzing}
            >
              Analyze document
            </Button>
          </Stack>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          <Grid size={12}>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2">Identifier</Typography>
                  <Typography variant="body1">
                    {detail?.identifier || "—"}
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2">Node ID</Typography>
                  <Typography variant="body1">
                    {detail?.nodeid || testSpecificationNodeId || "—"}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid size={12}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Typography variant="h6">Status</Typography>
              <Tooltip title="Reload status">
                <span>
                  <IconButton onClick={loadStatus} disabled={loadingStatus}>
                    <Refresh />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid size={12}>
                  <Typography>
                    <b>Stage:</b> {status?.stage || "—"}
                  </Typography>
                </Grid>

                <Grid size={12}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Box flex={1}>
                      <LinearProgress
                        variant="determinate"
                        value={status?.progress ? Number(status.progress) : 0}
                      />
                    </Box>
                    <Typography width={56} textAlign="right">
                      {status?.progress ? Number(status.progress) : 0}%
                    </Typography>
                  </Box>
                </Grid>

                <Grid size={12}>
                  <Typography variant="body2" color="text.secondary">
                    <b>Message:</b> {status?.message || "—"}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}
